import { useTheme } from '@emotion/react';
import { arrow, autoUpdate, flip, hide, offset, shift, useFloating } from '@floating-ui/react-dom';
import { useEffect, useRef, useState } from 'react';

import { convertPxTokenStringToNumber } from '@hrworks/design-system/functions';

import type { DropdownProps } from './Dropdown.types';
import { DropdownContext } from './DropdownContext';

export const Dropdown = ({ placement = 'bottom', ...otherProps }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const floatingOffset = convertPxTokenStringToNumber(theme.eclipseTier1.spacing['8px']);

  const {
    refs,
    floatingStyles,
    middlewareData,
    placement: currentPlacement,
  } = useFloating({
    middleware: [offset(floatingOffset + 2), flip({ padding: 10 }), shift(), arrow({ element: arrowRef }), hide()],
    placement,
    whileElementsMounted: autoUpdate,
  });

  const staticSide = currentPlacement.split('-')[0] as 'top' | 'right' | 'bottom' | 'left';

  const getTransformArrow = (side: typeof staticSide) => {
    const transformDegrees = {
      top: { deg: 0, translate: '' },
      right: { deg: 90, translate: 'translateY(-50%)' },
      bottom: { deg: 180, translate: '' },
      left: { deg: 270, translate: 'translateY(-50%)' },
    };
    const { deg, translate } = transformDegrees[side];
    return `rotate(${deg}deg) ${translate}`;
  };

  const arrowStyles = {
    position: 'absolute' as const,
    left: middlewareData.arrow?.x ? `${middlewareData.arrow.x}px` : staticSide === 'left' ? '100%' : '',
    top: middlewareData.arrow?.y ? `${middlewareData.arrow.y}px` : staticSide === 'top' ? '100%' : '',
    right: staticSide === 'right' ? '100%' : '',
    bottom: staticSide === 'bottom' ? '100%' : '',
    transform: getTransformArrow(staticSide),
  };

  useEffect(() => {
    const onEscapePress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !event.repeat) {
        event.stopPropagation();
        setIsOpen(false);
      }
    };

    const onClickOutside = (event: MouseEvent | FocusEvent | UIEvent) => {
      if (
        refs.floating.current &&
        refs.reference.current &&
        !refs.floating.current.contains(event.target as HTMLElement) &&
        !(refs.reference.current as HTMLElement).contains(event.target as HTMLElement)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscapePress);

    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscapePress);
    };
  }, [isOpen, refs.floating, refs.reference]);

  return (
    <DropdownContext.Provider
      value={{
        setReference: refs.setReference,
        setFloating: refs.setFloating,
        arrowRef,
        arrowStyles,
        floatingStyles: {
          ...floatingStyles,
          zIndex: theme.variables.zIndex.popover,
          ...(middlewareData.hide?.referenceHidden && { display: 'none' }),
        },
        isOpen,
        setIsOpen,
      }}
    >
      <div {...otherProps} />
    </DropdownContext.Provider>
  );
};
